import { Badge, createStyles, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Icons, Typography } from '@nutrien/cxp-components';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';

import { useMst } from '../../mobx-models/Root';
import { useRxDB } from '../../rxdb/useRxDB';
import { sentryCaptureException, useOnlineStatus } from '../../utilities';
import { useAIMSHeartbeat } from '../../utilities/hooks/useAIMSHeartbeat';
import { useFeatureFlags } from '../../utilities/useFeatureFlag';
import CustomAvatarIcon from '../AppBar/CustomAvatarIcon';
import LogoutModal from '../AppBar/LogoutModal';
import UserMenu from '../AppBar/UserMenu';

const useStyles = makeStyles(() =>
  createStyles({
    Icon: {
      cursor: 'pointer',
      paddingLeft: '20px',
    },
    grid2: {
      textAlign: 'center',
    },
    iconPos: {
      display: 'flex',
      alignSelf: 'center',
      cursor: 'pointer',
    },
    grid1: {
      paddingTop: '10px',
      background: '#272727',
      paddingBottom: '10px',
    },
    grid3: {
      textAlign: 'end',
      display: 'inline-flex',
    },
    cancelButton: {},
    saveButton: {
      fontSize: '14px',
      fontWeight: 700,
    },
    refreshIcon: {
      animation: '$icon-spin 2s infinite linear',
    },
    icon: {
      color: 'white',
    },
    '@-webkit-keyframes icon-spin': {
      '0%': {
        transform: 'rotate(0deg)',
      },
      '100%': {
        transform: 'rotate(-359deg)',
      },
    },
    '@keyframes icon-spin': {
      '0%': {
        transform: 'rotate(0deg)',
      },
      '100%': {
        transform: 'rotate(-359deg)',
      },
    },
  }),
);
interface Props {
  id: string;
  open: boolean;
  anchorEl: HTMLButtonElement | null;
  onClose: () => void;
  title: string;
}

const SignatureAndStampTopBar: React.FC<Props> = ({ onClose, title }) => {
  const classes = useStyles();
  const { syncData } = useMst();
  const { db } = useRxDB();
  const { sendAIMSHeartbeat } = useAIMSHeartbeat();
  const online = useOnlineStatus();
  const history = useHistory();
  const featureFlag: {
    [name: string]: string;
  } = useFeatureFlags();

  const [logoutOpen, setLogoutOpen] = useState<boolean>(false);
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<HTMLDivElement | null>(null);
  const [aimsDataSync, setAimsDataSync] = useState({ message: '', allowSync: true });

  const openUserMenu = (event: React.MouseEvent<HTMLDivElement>) =>
    setUserMenuAnchorEl(event.currentTarget);

  const handleUserMenuClose = () => setUserMenuAnchorEl(null);
  const onLogoutSelected = () => setLogoutOpen(true);

  const onLogout = async () => {
    await sendAIMSHeartbeat({
      logoutTime: dayjs(),
      comment: { location: 'SignatureAndStampTopBar' },
    });

    history.push('/logout');
  };

  const syncOnce = async () => {
    try {
      db?.startReplication({ isShowSync: true });
    } catch (error) {
      console.log('error in changing state', error); // eslint-disable-line no-console
      // console logs will be removed once sentry implementation is done
      sentryCaptureException(error, '~ file: SignatureAndStampTopBar.tsx ~ line 115 ~ syncOnce ~');
    }
  };

  const isSpinning = useMemo(() => {
    if (!online) return false;
    return localStorage.getItem('initialSync') === 'true' || syncData.isSyncing;
  }, [localStorage.getItem('initialSync') === 'true', online, syncData.isSyncing]);

  useEffect(() => {
    if (featureFlag && featureFlag?.aimsDataSync) {
      setAimsDataSync(JSON.parse(featureFlag.aimsDataSync));
    }
  }, [featureFlag]);

  return (
    <>
      <div>
        <Grid>
          <Grid id="alert-dialog-title">
            <Grid item container xs={12} className={classes.grid1}>
              <Grid xs={1} item onClick={onClose} className={classes.Icon}>
                <Icons.ArrowBackIcon />
              </Grid>
              <Grid xs={9} item className={classes.grid2}>
                <Typography variant="h5">{title}</Typography>
              </Grid>
              <Grid
                item
                xs={2}
                container
                justifyContent="flex-end"
                spacing={4}
                className={classes.grid3}
              >
                <Grid
                  item
                  className={classes.iconPos}
                  onClick={async () => {
                    if (online && !syncData.isSyncing && aimsDataSync?.allowSync) {
                      await syncOnce();
                    }
                  }}
                  data-testid="refreshIcon"
                >
                  <Badge>
                    <Icons.RefreshCcwFeather
                      color="primary"
                      size="small"
                      className={isSpinning ? classes.refreshIcon : ''}
                    />
                  </Badge>
                </Grid>
                <Grid item className={classes.iconPos}>
                  {online ? (
                    <Icons.WifiFeather color="primary" size="small" />
                  ) : (
                    <Icons.WifiOffFeather color="error" size="small" />
                  )}
                </Grid>
                <Grid
                  item
                  onClick={openUserMenu}
                  className={classes.iconPos}
                  data-testid="usermenuIcon"
                >
                  <Badge color="error">
                    <CustomAvatarIcon />
                  </Badge>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </div>
      <UserMenu
        id="user-menu"
        open={Boolean(userMenuAnchorEl)}
        anchorEl={userMenuAnchorEl}
        onClose={handleUserMenuClose}
        onLogout={onLogoutSelected}
      />

      <LogoutModal
        open={logoutOpen}
        onCancel={() => setLogoutOpen(false)}
        onLogout={onLogout}
        disableEnforceFocus={false}
      />
    </>
  );
};

export default observer(SignatureAndStampTopBar);
